/**
 * A new build replaced the hashed chunks this page was loaded with (a deploy while it was open, or an old cached
 * shell): a dynamic import then fails, and a town or the first night would stay black. The page reloads to pick up
 * the current build, at most once every `RELOAD_EVERY_MS`: a second failure sooner is left to each caller's fallback,
 * so a chunk that is really missing never loops, while a tab kept open for days still recovers from a later deploy.
 * Offline, a failed import is the network, not a deploy, so nothing reloads (the sitting's memory is kept).
 */
const KEY = 'li-chunk-reload';
/** How long after one automatic reload another may happen. */
export const RELOAD_EVERY_MS = 10 * 60_000;

/** Whether to reload now, given the stored time of the last automatic reload (null when none) and whether online. */
export function mayReload(last: string | null, now: number, online: boolean): boolean {
  if (!online) return false;
  if (last === null) return true;
  const at = Number(last);
  // An unreadable or future stamp counts as just now: never a loop.
  if (!Number.isFinite(at) || at > now) return false;
  return now - at >= RELOAD_EVERY_MS;
}

/** A failed dynamic import of a module script, as Chrome, Safari and Firefox word it. */
export function isChunkError(e: unknown): boolean {
  const m = e instanceof Error ? e.message : typeof e === 'string' ? e : '';
  return /dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i.test(m);
}

/** Reloads the page unless it did so in the last `RELOAD_EVERY_MS` or the device is offline; true when it does. */
export function reloadOnce(): boolean {
  try {
    if (!mayReload(sessionStorage.getItem(KEY), Date.now(), navigator.onLine !== false)) return false;
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch {
    return false;
  }
  location.reload();
  return true;
}

/** Vite's own signal for a failed preload or chunk, and any unhandled failed import, each reload the page once. */
export function watchChunks() {
  // The error is left to throw (no preventDefault): the caller falls back while the page reloads.
  window.addEventListener('vite:preloadError', () => { reloadOnce(); });
  window.addEventListener('unhandledrejection', (e) => { if (isChunkError(e.reason)) reloadOnce(); });
}
